import { type Ref, useId } from 'react';
import { useTexts } from '../../../../../../hooks/texts.hook';
import '../../../Sign/components/CountDownBar/style.scss';

function CountDownBar({
  timeLeft,
  initialTimeLeft,
  extendSign,
  extendButtonDisabled,
  extendButtonRef,
  className,
}: {
  timeLeft: number;
  initialTimeLeft: number;
  extendSign: () => void;
  extendButtonDisabled: boolean;
  extendButtonRef: Ref<HTMLButtonElement>;
  className?: string;
}) {
  const { t } = useTexts();
  const descriptionId = useId();
  const showExtendSignButton = timeLeft <= 60;
  // Round up to whole minutes above 60 seconds, then to 10-second steps above 30 seconds; show exact seconds thereafter.
  const displayTime = timeLeft > 60 ? Math.ceil(timeLeft / 60) : timeLeft > 30 ? Math.ceil(timeLeft / 10) * 10 : timeLeft;

  function getAnnouncement(): string {
    if (timeLeft === 240) return t('Fyra minuter återstår.', 'Four minutes remaining.');
    if (timeLeft === 180) return t('Tre minuter återstår.', 'Three minutes remaining.');
    if (timeLeft === 120) return t('Två minuter återstår.', 'Two minutes remaining.');
    if (timeLeft === 60) return t('60 sekunder återstår. Du kan förlänga tiden.', '60 seconds remaining. You can extend the time.');
    if (timeLeft === 50) return t('50 sekunder återstår.', '50 seconds remaining.');
    if (timeLeft === 40) return t('40 sekunder återstår.', '40 seconds remaining.');
    if (timeLeft === 30) return t('30 sekunder återstår.', '30 seconds remaining.');
    if (timeLeft === 20) return t('20 sekunder återstår.', '20 seconds remaining.');
    if (timeLeft === 10) return t('10 sekunder återstår.', '10 seconds remaining.');
    return '';
  }

  return (
    <div className={`count-down-bar-wrapper ${className ?? ''}`}>
      <div className="progress rounded-pill mb-1">
        <div
          className="progress-bar"
          role="progressbar"
          aria-label={t('Återstående tid för BankID-signering', 'Time remaining for BankID signing')}
          aria-valuenow={timeLeft}
          aria-valuemin={0}
          aria-valuemax={initialTimeLeft}
          aria-valuetext={`${timeLeft} ${t('sekunder återstår', 'seconds remaining')}`}
          style={{ width: `${(timeLeft / initialTimeLeft) * 100}%` }}
        ></div>
      </div>
      <div className="time-left">
        <span className="time-left__text" aria-hidden="true">
          {displayTime} {timeLeft > 60 ? t('minuter kvar', 'minutes left') : t('sekunder kvar', 'seconds left')}
        </span>
      </div>
      <span className="visually-hidden" id={descriptionId}>
        {showExtendSignButton && t('Förläng tiden för BankID-signeringen.', 'Extend the time for BankID signing.')}
      </span>
      <span className="visually-hidden" role="status" aria-atomic="true">
        {getAnnouncement()}
      </span>
      {showExtendSignButton && (
        <button ref={extendButtonRef} type="button" className="btn btn-primary mt-2 w-100" aria-describedby={descriptionId} onClick={extendSign} disabled={extendButtonDisabled}>
          {t('Förläng tiden', 'Extend time')}
        </button>
      )}
    </div>
  );
}

export default CountDownBar;
